/**
 * Notification State Store
 *
 * Store for notification history and state tracking, cached in localStorage so a
 * page reload does not lose notifications the user still has to answer. A Flow
 * waiting on a button press keeps waiting on the server across a reload, so the
 * browser must be able to show that prompt again.
 *
 * The cache is a local convenience, not a source of truth: the server decides
 * whether a response is still accepted, and answering a prompt that already timed
 * out reports back as already handled.
 */

import { devLog } from '../utils/devLog';
const STORAGE_KEY = 'notif_store_v1';
// Long enough to survive a reload or a browser restart; short enough that prompts
// nobody answered do not accumulate forever.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_PERSISTED = 50;

function hasStorage() {
  try {
    return typeof localStorage !== 'undefined';
  } catch (_) {
    return false;
  }
}

function notificationAge(notification) {
  const ts = Date.parse(notification?.timestamp);
  return Number.isFinite(ts) ? Date.now() - ts : 0;
}

class NotificationStore {
  constructor() {
    this.notifications = this._loadPersisted();
    this.listeners = new Set();
    this.maxNotifications = 100; // Limit history size
  }

  _loadPersisted() {
    if (!hasStorage()) return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((n) => n && n.id && notificationAge(n) < MAX_AGE_MS)
        .slice(0, MAX_PERSISTED);
    } catch (_) {
      return [];
    }
  }

  _persist() {
    if (!hasStorage()) return;
    try {
      // The raw event is only kept for in-session reference and can be large, so it
      // is left out of the cache rather than risking the storage quota.
      const lean = this.notifications
        .filter((n) => notificationAge(n) < MAX_AGE_MS)
        .slice(0, MAX_PERSISTED)
        .map((n) => {
          const copy = { ...n };
          delete copy.event;
          return copy;
        });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lean));
    } catch (_) {
      // A full or unavailable quota must never break notification delivery.
    }
  }

  /**
   * Add a notification to the store
   */
  addNotification(event) {
    // Parse responses from push data (may be JSON string from FCM)
    let responses = event.responses || event.context?.meta?.responses || null;
    if (typeof responses === 'string') {
      try { responses = JSON.parse(responses); } catch (_) { responses = null; }
    }
    if (!Array.isArray(responses) || responses.length === 0) responses = null;

    const notification = {
      id: event.id || event.eventId || `notif-${Date.now()}`,
      severity: event.severity || 'info',
      source: event.source || 'system',
      type: event.type || 'notification',
      message: this._buildMessage(event),
      title: this._buildTitle(event),
      timestamp: event.timestamp || new Date().toISOString(),
      read: false,
      acknowledged: false,
      ackRequired: event.alertMeta?.ackRequired || false,
      alertMeta: event.alertMeta || null,
      actionId: event.actionId || event.context?.meta?.actionId || null,
      responses: responses,
      responseStatus: null, // null | 'pending' | 'accepted' | 'rejected' | 'error'
      responseMessage: null,
      modalDismissed: false, // true when user manually dismisses the response modal
      event: event // Store full event for reference
    };

    // Check for duplicate
    if (this.notifications.some(n => n.id === notification.id)) {
      devLog('[NOTIF-STORE] Skipping duplicate notification:', notification.id);
      return null;
    }

    // Add to beginning (newest first)
    this.notifications.unshift(notification);

    // Limit size
    if (this.notifications.length > this.maxNotifications) {
      this.notifications = this.notifications.slice(0, this.maxNotifications);
    }

    devLog('[NOTIF-STORE] Added notification:', {
      id: notification.id,
      severity: notification.severity,
      total: this.notifications.length
    });

    this._notifyListeners();
    return notification;
  }

  /**
   * Mark notification as read
   */
  markAsRead(id) {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.read = true;
      devLog('[NOTIF-STORE] Marked as read:', id);
      this._notifyListeners();
    }
  }

  /**
   * Mark notification as acknowledged (for critical)
   */
  markAsAcknowledged(id) {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.acknowledged = true;
      notification.read = true;
      devLog('[NOTIF-STORE] Marked as acknowledged:', id);
      this._notifyListeners();
    }
  }

  /**
   * Mark notification response as submitted (UI disablement)
   * @param {string} id - Notification ID
   * @param {'pending'|'accepted'|'rejected'|'error'} status
   * @param {string} [message] - Optional status message
   */
  markAsResponded(id, status, message = null, extra = null) {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.responseStatus = status;
      notification.responseMessage = message;
      notification.responseKind = extra?.responseKind || null;
      notification.pendingResponseKey = extra?.pendingResponseKey || null;
      notification.attemptedByUid = extra?.attemptedByUid || null;
      if (status === 'accepted' || status === 'rejected') {
        notification.read = true;
        notification.pendingResponseKey = null;
        notification.attemptedByUid = null;
      }
      devLog('[NOTIF-STORE] Marked response status:', { id, status, message });
      this._notifyListeners();
    }
  }

  clearResponseAttempt(id) {
    const notification = this.notifications.find(n => n.id === id);
    if (!notification?.attemptedByUid && !notification?.pendingResponseKey) return;
    notification.responseStatus = null;
    notification.responseMessage = null;
    notification.responseKind = null;
    notification.pendingResponseKey = null;
    notification.attemptedByUid = null;
    this._notifyListeners();
  }

  /**
   * Dismiss the response modal for a notification (user manually closed it)
   * @param {string} id - Notification ID
   */
  dismissModal(id) {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.modalDismissed = true;
      notification.read = true;
      devLog('[NOTIF-STORE] Modal dismissed:', id);
      this._notifyListeners();
    }
  }

  /**
   * Get notifications for the response modal stack (not dismissed, not fully handled).
   * Includes ALL notifications — those with response buttons show action buttons,
   * those without show just a close button.
   * Returns newest first, emergency severity always sorted to top.
   */
  getActionableNotifications() {
    const actionable = this.notifications.filter(n =>
      !n.modalDismissed &&
      n.responseStatus !== 'accepted' &&
      n.responseStatus !== 'rejected'
    );
    // Sort: emergency first, then by timestamp (newest first — already in order)
    actionable.sort((a, b) => {
      const sevOrder = { critical: 0, error: 1, warning: 2, info: 3 };
      const aOrder = sevOrder[a.severity] ?? 3;
      const bOrder = sevOrder[b.severity] ?? 3;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return 0; // preserve existing newest-first order
    });
    return actionable;
  }

  /**
   * Notifications that still need the user, whether or not their card is on screen.
   *
   * A prompt with response buttons keeps counting after the card is dismissed,
   * because the Flow behind it is still waiting for an answer and dismissing the
   * card is not answering it. A plain notification with nothing to decide stops
   * counting once dismissed.
   */
  getPendingNotifications() {
    return this.notifications.filter((n) => {
      if (n.responseStatus === 'accepted' || n.responseStatus === 'rejected') return false;
      const awaitsAnswer = Array.isArray(n.responses) && n.responses.length > 0;
      return awaitsAnswer || !n.modalDismissed;
    });
  }

  getPendingCount() {
    return this.getPendingNotifications().length;
  }

  /**
   * Put every still-pending notification back on screen, so a prompt that was
   * dismissed or lost to a reload can be answered again.
   */
  reopenPending() {
    let changed = 0;
    this.getPendingNotifications().forEach((n) => {
      if (n.modalDismissed) {
        n.modalDismissed = false;
        changed += 1;
      }
    });
    devLog('[NOTIF-STORE] Reopened pending notifications:', changed);
    if (changed) this._notifyListeners();
    return changed;
  }

  /**
   * Get all notifications
   */
  getAll() {
    return [...this.notifications];
  }

  /**
   * Get unread count
   */
  getUnreadCount() {
    return this.notifications.filter(n => !n.read).length;
  }

  /**
   * Get unacknowledged critical count
   */
  getUnacknowledgedCriticalCount() {
    return this.notifications.filter(
      n => n.severity === 'critical' && !n.acknowledged
    ).length;
  }

  /**
   * Get badge count (unread + unacknowledged critical)
   */
  getBadgeCount() {
    const unread = this.notifications.filter(n => !n.read && n.severity !== 'critical').length;
    const unackedCritical = this.getUnacknowledgedCriticalCount();
    return unread + unackedCritical;
  }

  /**
   * Get current warning (if any)
   */
  getCurrentWarning() {
    return this.notifications.find(n => n.severity === 'warning' && !n.read);
  }

  /**
   * Get unacknowledged critical notifications
   */
  getUnacknowledgedCritical() {
    return this.notifications.filter(
      n => n.severity === 'critical' && !n.acknowledged
    );
  }

  /**
   * Subscribe to changes
   */
  subscribe(listener) {
    this.listeners.add(listener);
    devLog('[NOTIF-STORE] SUBSCRIBED', {
      listenerCount: this.listeners.size,
      currentNotifications: this.notifications.length
    });
    return () => {
      this.listeners.delete(listener);
      devLog('[NOTIF-STORE] UNSUBSCRIBED', {
        listenerCount: this.listeners.size
      });
    };
  }

  /**
   * Clear all notifications (for testing)
   */
  clear() {
    this.notifications = [];
    devLog('[NOTIF-STORE] Cleared all notifications');
    this._notifyListeners();
  }

  /**
   * Build message from event
   */
  _buildMessage(event) {
    const explicit = event?.context?.meta?.message || event?.context?.message || event?.message;
    if (explicit) return String(explicit);
    return 'New notification received';
  }

  /**
   * Build title from event
   */
  _buildTitle(event) {
    const explicit = event?.context?.meta?.title || event?.context?.title || event?.title;
    if (explicit) return String(explicit);
    const severity = event.severity || 'info';
    if (severity === 'critical' || severity === 'error') {
      return `${severity.toUpperCase()} Alert`;
    }
    return 'Notification';
  }

  /**
   * Notify all listeners
   */
  _notifyListeners() {
    this._persist();
    devLog('[NOTIF-STORE] NOTIFYING LISTENERS', {
      listenerCount: this.listeners.size,
      notificationCount: this.notifications.length,
      latestNotification: this.notifications[0] ? {
        id: this.notifications[0].id,
        severity: this.notifications[0].severity
      } : null
    });
    
    this.listeners.forEach((listener, index) => {
      try {
        devLog(`[NOTIF-STORE] Calling listener ${index + 1}/${this.listeners.size}`);
        listener(this.notifications);
      } catch (e) {
        console.error('[NOTIF-STORE] Listener error:', e?.message || 'listener failed');
      }
    });
    
    devLog('[NOTIF-STORE] All listeners notified');
  }
}

// Singleton instance
const notificationStore = new NotificationStore();

export default notificationStore;
